import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsAPI } from '../services/api';
import api from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const SOURCE_DISPLAY = { fb: 'Facebook', shopee: 'Shopee', lazada: 'Lazada', gkash: 'Gkash', checkout: 'Website', admin: 'Admin', woocommerce: 'Website', landingpage: 'Landing Page', offlineret: 'Offline', shopcom: 'Shopcom', 'walk-in': 'Walk-in', walkin: 'Walk-in' };
const normalizeSource = (s) => { if (!s) return s; return SOURCE_DISPLAY[s.toLowerCase()] || s.charAt(0).toUpperCase() + s.slice(1).toLowerCase(); };

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  ShoppingCart,
  DollarSign,
  CreditCard,
  RefreshCw,
  Copy,
  FileText,
  Loader2,
  Check,
  Pencil,
  X,
  Plus,
  Trash2,
  MessageSquare,
  Calendar
} from 'lucide-react';

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [qpointId, setQpointId] = useState('');
  const [isEditingQpoint, setIsEditingQpoint] = useState(false);
  const [tempQpointId, setTempQpointId] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Tracking edit state
  const [editingTracking, setEditingTracking] = useState(null); // order id
  const [trackingInput, setTrackingInput] = useState('');
  const [savingTracking, setSavingTracking] = useState(false);

  const handleSaveTracking = async (orderId) => {
    setSavingTracking(true);
    try {
      await api.put(`/orders/${orderId}/tracking`, { tracking_number: trackingInput });
      setEditingTracking(null);
      setTrackingInput('');
      // Refetch orders to show updated tracking
      const response = await api.get(`/clients/${id}/orders`);
      setOrders(response.data || []);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving tracking');
    }
    setSavingTracking(false);
  };
  
  // Edit client details state
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: ''
  });
  const [savingClient, setSavingClient] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Matched username state (for Shopee/Lazada)
  const [matchedUsername, setMatchedUsername] = useState('');
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [tempUsername, setTempUsername] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);
  
  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  
  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const response = await clientsAPI.getOne(id);
        setClient(response.data);
        setStatus(response.data.status);
        setQpointId(response.data.qpoint_id || '');
        setMatchedUsername(response.data.matched_username || '');
        setNotes(response.data.notes || []);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchClient();
  }, [id]);

  // Fetch orders separately
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await api.get(`/clients/${id}/orders`);
        setOrders(response.data || []);
      } catch (error) {
      } finally {
        setOrdersLoading(false);
      }
    };
    if (id) {
      fetchOrders();
    }
  }, [id]);

  const refreshOrders = async () => {
    setOrdersLoading(true);
    try {
      // First sync orders from WooCommerce for this specific client
      const syncResponse = await api.post(`/clients/${id}/sync-orders`);
      
      // Then fetch the updated orders
      const response = await api.get(`/clients/${id}/orders`);
      setOrders(response.data || []);
      
      // Show result message
      if (syncResponse.data.orders_found > 0) {
        alert(`✓ Synced ${syncResponse.data.orders_found} orders from WooCommerce`);
      } else if (syncResponse.data.potential_mismatches && syncResponse.data.potential_mismatches.length > 0) {
        const mismatches = syncResponse.data.potential_mismatches;
        alert(`No orders found for this client's phone number.\n\nPotential data issue detected:\nFound orders with similar name but different phone numbers.\n\nThis may indicate incorrect phone number in WooCommerce customer data.\n\nMismatched phones found: ${mismatches.map(m => m.order_phone).join(', ')}`);
      } else {
        alert('No orders found for this client in WooCommerce.');
      }
    } catch (error) {
      alert('Error syncing orders. Please try again.');
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await clientsAPI.update(id, { status: newStatus });
      setStatus(newStatus);
      setClient({ ...client, status: newStatus });
    } catch (error) {
    }
  };

  const handleEditQpoint = () => {
    setTempQpointId(qpointId);
    setIsEditingQpoint(true);
  };

  const handleSaveQpoint = async () => {
    setSaving(true);
    try {
      await clientsAPI.update(id, { qpoint_id: tempQpointId });
      setQpointId(tempQpointId);
      setClient({ ...client, qpoint_id: tempQpointId });
      setIsEditingQpoint(false);
    } catch (error) {
    } finally {
      setSaving(false);
    }
  };

  const handleCancelQpoint = () => {
    setTempQpointId(qpointId);
    setIsEditingQpoint(false);
  };

  // Matched Username handlers
  const handleEditUsername = () => {
    setTempUsername(matchedUsername);
    setIsEditingUsername(true);
  };

  const handleSaveUsername = async () => {
    setSavingUsername(true);
    try {
      await api.put(`/clients/${id}/matched-username`, { matched_username: tempUsername });
      setMatchedUsername(tempUsername);
      setClient({ ...client, matched_username: tempUsername });
      setIsEditingUsername(false);
    } catch (error) {
    } finally {
      setSavingUsername(false);
    }
  };

  const handleCancelUsername = () => {
    setTempUsername(matchedUsername);
    setIsEditingUsername(false);
  };

  const [copiedId, setCopiedId] = useState(null);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Notes functions
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      const response = await api.post(`/clients/${id}/notes`, { note: newNote });
      setNotes([...notes, response.data.note]);
      setNewNote('');
    } catch (error) {
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/clients/${id}/notes/${noteId}`);
      setNotes(notes.filter(n => n.id !== noteId));
    } catch (error) {
    }
  };

  // Edit client handlers
  const handleEditClient = () => {
    setEditFormData({
      name: client?.name || '',
      phone: client?.phone || '',
      email: client?.email || '',
      location: client?.location || ''
    });
    setIsEditingClient(true);
  };

  const handleSaveClient = async () => {
    setSavingClient(true);
    try {
      // Normalize phone number
      const normalizedPhone = editFormData.phone.replace(/[^0-9+]/g, '');
      
      await clientsAPI.update(id, {
        name: editFormData.name,
        phone: editFormData.phone,
        email: editFormData.email,
        location: editFormData.location,
        normalized_phone: normalizedPhone
      });
      
      setClient({
        ...client,
        ...editFormData,
        normalized_phone: normalizedPhone
      });
      setIsEditingClient(false);
    } catch (error) {
      alert('Error saving client. Please try again.');
    } finally {
      setSavingClient(false);
    }
  };

  const handleCancelEditClient = () => {
    setIsEditingClient(false);
  };

  const handleDeleteClient = async () => {
    if (!window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      await clientsAPI.delete(id);
      navigate('/clients');
    } catch (error) {
      alert('Error deleting client. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Format relative date
  const formatRelativeDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `about ${Math.floor(diffDays / 7)} week(s) ago`;
    if (diffDays < 365) return `about ${Math.floor(diffDays / 30)} month(s) ago`;
    return `about ${Math.floor(diffDays / 365)} year(s) ago`;
  };

  // Get status badge color
  const getStatusColor = (orderStatus) => {
    const status = orderStatus?.toLowerCase() || '';
    if (status.includes('completed') || status.includes('delivered')) return 'bg-green-100 text-green-700';
    if (status.includes('processing') || status.includes('pending')) return 'bg-yellow-100 text-yellow-700';
    if (status.includes('shipped') || status.includes('sent') || status.includes('packed')) return 'bg-blue-100 text-blue-700';
    if (status.includes('cancelled') || status.includes('failed') || status.includes('refunded')) return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Client not found</p>
        <Button onClick={() => navigate('/clients')} className="mt-4">
          Back to Clients
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/clients')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="text-gray-600" size={20} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">{client.name}</h1>
            </div>
            <div className="flex items-center gap-4 mt-1 flex-wrap">
              <p className="text-gray-500">
                {client.woo_customer_id ? `WooCommerce ID: ${client.woo_customer_id}` : `CRM ID: ${client.crm_id || client.id.slice(0, 8)}`}
              </p>
              
              {/* Qpoint ID Field */}
              <div className="flex items-center gap-2">
                <span className="text-gray-400">|</span>
                <span className="text-gray-500">Qpoint ID:</span>
                {isEditingQpoint ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={tempQpointId}
                      onChange={(e) => setTempQpointId(e.target.value)}
                      placeholder="Enter Qpoint ID"
                      className="h-8 w-32 text-sm"
                      autoFocus
                    />
                    <button
                      onClick={handleSaveQpoint}
                      disabled={saving}
                      className="p-1 hover:bg-green-100 rounded transition-colors"
                    >
                      <Check className="text-green-600" size={16} />
                    </button>
                    <button
                      onClick={handleCancelQpoint}
                      className="p-1 hover:bg-red-100 rounded transition-colors"
                    >
                      <X className="text-red-500" size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${qpointId ? 'text-gray-900' : 'text-gray-400 italic'}`}>
                      {qpointId || 'Not set'}
                    </span>
                    <button
                      onClick={handleEditQpoint}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      title="Edit Qpoint ID"
                    >
                      <Pencil className="text-gray-400" size={14} />
                    </button>
                  </div>
                )}
              </div>

              {/* Matched Username Field (Shopee/Lazada) */}
              <div className="flex items-center gap-2">
                <span className="text-gray-400">|</span>
                <span className="text-gray-500">Shopee/Lazada:</span>
                {isEditingUsername ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={tempUsername}
                      onChange={(e) => setTempUsername(e.target.value)}
                      placeholder="Enter username"
                      className="h-8 w-32 text-sm"
                      autoFocus
                    />
                    <button
                      onClick={handleSaveUsername}
                      disabled={savingUsername}
                      className="p-1 hover:bg-green-100 rounded transition-colors"
                    >
                      <Check className="text-green-600" size={16} />
                    </button>
                    <button
                      onClick={handleCancelUsername}
                      className="p-1 hover:bg-red-100 rounded transition-colors"
                    >
                      <X className="text-red-500" size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${matchedUsername ? 'text-gray-900' : 'text-gray-400 italic'}`}>
                      {matchedUsername || 'Not linked'}
                    </span>
                    <button
                      onClick={handleEditUsername}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                      title="Link Shopee/Lazada/Tiktok username"
                    >
                      <Pencil className="text-gray-400" size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Select value={status} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[120px] border-gray-200">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="New">New</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="VIP">VIP</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Contact and Order Summary */}
      <div className="grid grid-cols-2 gap-6">
        {/* Contact Information */}
        <Card className="p-6 bg-white border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Contact Information</h2>
            <div className="flex items-center gap-2">
              {!isEditingClient ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEditClient}
                    data-testid="edit-client-btn"
                  >
                    <Pencil size={14} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeleteClient}
                    disabled={deleting}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-client-btn"
                  >
                    {deleting ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    onClick={handleSaveClient}
                    disabled={savingClient}
                    className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                  >
                    {savingClient ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} className="mr-1" />}
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelEditClient}
                  >
                    <X size={14} />
                  </Button>
                </>
              )}
            </div>
          </div>
          
          {isEditingClient ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Name</label>
                <Input
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  placeholder="Client name"
                />
              </div>
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Email</label>
                <Input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                  placeholder="client@example.com"
                />
              </div>
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Phone</label>
                <Input
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                  placeholder="+60123456789"
                />
              </div>
              <div>
                <label className="text-sm text-gray-500 mb-1 block">Location</label>
                <Input
                  value={editFormData.location}
                  onChange={(e) => setEditFormData({...editFormData, location: e.target.value})}
                  placeholder="Kuala Lumpur, MY"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
                  <Mail className="text-gray-400" size={18} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-gray-900">{client.email || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
                  <Phone className="text-gray-400" size={18} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="text-gray-900">{client.phone || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
                  <MapPin className="text-gray-400" size={18} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="text-gray-900">{client.location || '-'}</p>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Order Summary */}
        <Card className="p-6 bg-white border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Order Summary</h2>
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                <ShoppingCart className="text-purple-500" size={18} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{orders.length || client.orders || 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                <DollarSign className="text-green-500" size={18} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Spend</p>
                <p className="text-2xl font-bold text-gray-900">
                  RM{(orders.length > 0 
                    ? orders.reduce((sum, o) => sum + (o.total || 0), 0) 
                    : (client.total_spend || 0)
                  ).toFixed(2)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                <CreditCard className="text-blue-500" size={18} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Order Sources</p>
                <p className="text-gray-900">
                  {orders.length > 0 
                    ? [...new Set(orders.map(o => normalizeSource(o.order_source)).filter(Boolean))].join(', ') || 'Website'
                    : 'No orders yet'}
                </p>
              </div>
            </div>
            {client.first_order_date && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                  <Calendar className="text-amber-500" size={18} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">First Purchase</p>
                  <p className="text-gray-900 font-medium">
                    {new Date(client.first_order_date).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Order History */}
      <Card className="bg-white border border-gray-100">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Order History</h2>
          <Button 
            variant="outline" 
            className="h-9 border-[#4F46E5] text-[#4F46E5] hover:bg-[#4F46E5] hover:text-white"
            onClick={refreshOrders}
            disabled={ordersLoading}
          >
            {ordersLoading ? (
              <Loader2 size={14} className="mr-2 animate-spin" />
            ) : (
              <RefreshCw size={14} className="mr-2" />
            )}
            Sync from WooCommerce
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Order ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Order Source</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tracking Number</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ordersLoading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center">
                    <Loader2 className="animate-spin mx-auto text-[#4F46E5]" size={24} />
                  </td>
                </tr>
              ) : orders.length > 0 ? orders.map((order, index) => (
                <tr key={order.id || index} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 font-medium">{order.order_id}</span>
                      <button onClick={() => copyToClipboard(order.order_id)} className="p-1 hover:bg-gray-100 rounded" data-testid={`copy-order-id-${index}`}>
                        {copiedId === order.order_id ? <Check className="text-green-500" size={14} /> : <Copy className="text-gray-400" size={14} />}
                      </button>
                    </div>
                    {order.items && order.items.length > 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        {order.items.map(item => `${item.sku || item.name} × ${item.quantity}`).join(', ')}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-gray-900">{formatRelativeDate(order.date_created)}</p>
                    <p className="text-sm text-gray-500">{order.date}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-gray-600">{normalizeSource(order.order_source) || '-'}</td>
                  <td className="px-4 py-4">
                    {editingTracking === (order.id || order.woo_order_id) ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={trackingInput}
                          onChange={(e) => setTrackingInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTracking(order.id || order.woo_order_id); if (e.key === 'Escape') setEditingTracking(null); }}
                          className="border border-gray-300 rounded px-2 py-1 text-sm w-36"
                          placeholder="Enter tracking..."
                          autoFocus
                          data-testid={`tracking-input-${index}`}
                        />
                        <button onClick={() => handleSaveTracking(order.id || order.woo_order_id)} disabled={savingTracking} className="p-1 text-green-600 hover:bg-green-50 rounded">
                          {savingTracking ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                        </button>
                        <button onClick={() => setEditingTracking(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                          <X size={14} />
                        </button>
                      </div>
                    ) : order.tracking_number ? (
                      <div className="flex items-center gap-1">
                        <span className="text-gray-900">{order.tracking_number}</span>
                        <button onClick={() => copyToClipboard(order.tracking_number)} className="p-1 hover:bg-gray-100 rounded" data-testid={`copy-tracking-${index}`}>
                          {copiedId === order.tracking_number ? <Check className="text-green-500" size={14} /> : <Copy className="text-gray-400" size={14} />}
                        </button>
                        <button onClick={() => { setEditingTracking(order.id || order.woo_order_id); setTrackingInput(order.tracking_number); }} className="p-1 text-gray-400 hover:text-[#4F46E5] hover:bg-gray-100 rounded" data-testid={`edit-tracking-${index}`}>
                          <Pencil size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditingTracking(order.id || order.woo_order_id); setTrackingInput(''); }}
                        className="text-[#4F46E5] text-sm hover:underline"
                        data-testid={`add-tracking-${index}`}
                      >
                        + Add Tracking
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-4 font-medium text-gray-900">RM{(order.total || 0).toFixed(2)}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No orders found. Sync orders from WooCommerce to see them here.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Customer Notes */}
      <Card className="p-6 bg-white border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="text-gray-500" size={18} />
            <h2 className="text-lg font-semibold text-gray-900">Customer Notes</h2>
          </div>
        </div>
        
        {/* Add Note Form */}
        <div className="mb-4">
          <div className="flex gap-2">
            <Textarea
              placeholder="Add a note about this customer..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="flex-1 min-h-[80px]"
            />
          </div>
          <div className="mt-2 flex justify-end">
            <Button 
              onClick={handleAddNote}
              disabled={!newNote.trim() || addingNote}
              className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
            >
              {addingNote ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <Plus size={14} className="mr-2" />
              )}
              Add Note
            </Button>
          </div>
        </div>

        {/* Notes List */}
        <div className="space-y-3">
          {notes.length > 0 ? notes.map((note) => (
            <div key={note.id} className="bg-gray-50 rounded-lg p-4 relative group">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <p className="text-gray-700 whitespace-pre-wrap">{note.note}</p>
                  <div className="mt-2 text-sm text-gray-500">
                    <span className="font-medium">{note.user}</span>
                    <span className="mx-2">•</span>
                    <span>{new Date(note.timestamp).toLocaleString()}</span>
                    {note.edited_at && (
                      <span className="ml-2 text-gray-400">(edited)</span>
                    )}
                  </div>
                </div>
                <button 
                  onClick={() => handleDeleteNote(note.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-opacity"
                  title="Delete note"
                >
                  <Trash2 className="text-red-500" size={14} />
                </button>
              </div>
            </div>
          )) : (
            <p className="text-gray-500 text-center py-4">No notes yet. Add a note about this customer.</p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ClientDetailPage;
